import { useState, useEffect } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Shield, Users, CalendarDays, Crown, Zap, Gem, Trash2, Gift, RotateCcw, Search, Ban, CheckCircle, Plus, MailCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_STYLES = {
  start: { label: 'Старт', cls: 'bg-stone-800 text-stone-400 border border-stone-700', icon: Zap },
  practic: { label: 'Практик', cls: 'bg-amber-900/30 text-amber-400 border border-amber-900/50', icon: Crown },
  profi: { label: 'Профи', cls: 'bg-violet-900/30 text-violet-400 border border-violet-900/50', icon: Gem },
  clinic: { label: 'Организация', cls: 'bg-teal-900/30 text-teal-400 border border-teal-900/50', icon: Crown },
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem('admin_token') || '');
  const [authed, setAuthed] = useState(false);
  const [key, setKey] = useState('');
  const [psychologists, setPsychologists] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [giftOpen, setGiftOpen] = useState(null);
  const [giftPlan, setGiftPlan] = useState('practic');
  const [giftDays, setGiftDays] = useState(30);
  const [disabledPlans, setDisabledPlans] = useState([]);
  const [closedBeta, setClosedBeta] = useState(false);
  const [betaRecruiting, setBetaRecruiting] = useState(false);
  const [betaSignups, setBetaSignups] = useState([]);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'doctor', plan: 'start', custom_max_doctors: '', custom_max_admins: '' });
  const [demoGenerating, setDemoGenerating] = useState(false);
  const [demoProgress, setDemoProgress] = useState('');
  const [demoLog, setDemoLog] = useState([]);
  const [demoPercent, setDemoPercent] = useState(0);
  const [notifyTg, setNotifyTg] = useState('715022267');
  const [notifyEmail, setNotifyEmail] = useState('wwdd221@mail.ru');

  const adminHeaders = () => ({ Authorization: `Bearer ${token}` });

  const login = async () => {
    try {
      const { data } = await api.post(`/admin/login`, { password: key });
      localStorage.setItem('admin_token', data.token);
      setToken(data.token);
      setAuthed(true);
    } catch { toast.error('Неверный ключ'); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pRes, sRes, cRes] = await Promise.all([
        api.get(`/admin/psychologists`, { headers: adminHeaders() }),
        api.get(`/admin/stats`, { headers: adminHeaders() }),
        api.get(`/admin/config`, { headers: adminHeaders() }),
      ]);
      setPsychologists(pRes.data);
      setStats(sRes.data);
      setDisabledPlans(cRes.data.disabled_plans || []);
      setClosedBeta(cRes.data.closed_beta || false);
      setBetaRecruiting(cRes.data.beta_recruiting || false);
      setNotifyTg(cRes.data.service_notify_telegram || '715022267');
      setNotifyEmail(cRes.data.service_notify_email || 'wwdd221@mail.ru');
      // Fetch beta signups
      try {
        const bRes = await api.get('/admin/beta-signups', { headers: adminHeaders() });
        setBetaSignups(bRes.data);
      } catch {}
      setAuthed(true);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setAuthed(false);
        localStorage.removeItem('admin_token');
      }
    }
    setLoading(false);
  };

  useEffect(() => { if (token) fetchData(); }, [token]);

  const giftPlanTo = async () => {
    if (!giftOpen) return;
    try {
      await api.put(`/admin/psychologists/${giftOpen}/plan?plan_id=${giftPlan}&days=${giftDays}`, {}, { headers: adminHeaders() });
      toast.success(`${PLAN_STYLES[giftPlan].label} подарен${giftDays ? ` на ${giftDays} дн.` : ''}`);
      setGiftOpen(null);
      fetchData();
    } catch { toast.error('Ошибка'); }
  };

  const resetPlan = async (id) => {
    try {
      await api.put(`/admin/psychologists/${id}/plan?plan_id=start&days=0`, {}, { headers: adminHeaders() });
      toast.success('Сброшен на Старт');
      fetchData();
    } catch { toast.error('Ошибка'); }
  };

  const toggleBan = async (id, ban) => {
    try {
      await api.put(`/admin/psychologists/${id}/ban?banned=${ban}`, {}, { headers: adminHeaders() });
      toast.success(ban ? 'Заблокирован' : 'Разблокирован');
      fetchData();
    } catch { toast.error('Ошибка'); }
  };

  const deletePsych = async (id, name) => {
    if (!confirm(`Отключить аккаунт ${name} и поставить рабочие данные в очередь удаления по настроенной политике хранения?`)) return;
    try {
      const { data } = await api.delete(`/admin/psychologists/${id}`, { headers: adminHeaders() });
      toast.success(data.retention_status === 'pending_policy'
        ? 'Аккаунт отключён. Для удаления данных нужно назначить политику хранения.'
        : 'Аккаунт отключён, удаление данных запланировано.');
      fetchData();
    } catch { toast.error('Ошибка'); }
  };

  const verifyEmail = async (id) => {
    try {
      await api.put(`/admin/psychologists/${id}/verify-email`, {}, { headers: adminHeaders() });
      toast.success('Почта подтверждена');
      fetchData();
    } catch { toast.error('Ошибка'); }
  };

  const togglePlanDisabled = async (planId) => {
    const next = disabledPlans.includes(planId)
      ? disabledPlans.filter(p => p !== planId)
      : [...disabledPlans, planId];
    try {
      await api.put(`/admin/config?disabled_plans=${next.join(',')}&closed_beta=${closedBeta}`, {}, { headers: adminHeaders() });
      setDisabledPlans(next);
      toast.success('Обновлено');
    } catch { toast.error('Ошибка'); }
  };

  const toggleBeta = async () => {
    const next = !closedBeta;
    try {
      await api.put(`/admin/config?disabled_plans=${disabledPlans.join(',')}&closed_beta=${next}&beta_recruiting=${betaRecruiting}`, {}, { headers: adminHeaders() });
      setClosedBeta(next);
      toast.success(next ? 'Закрытая бета включена' : 'Регистрация открыта');
    } catch { toast.error('Ошибка'); }
  };

  const toggleRecruiting = async () => {
    const next = !betaRecruiting;
    try {
      await api.put(`/admin/config?disabled_plans=${disabledPlans.join(',')}&closed_beta=${closedBeta}&beta_recruiting=${next}`, {}, { headers: adminHeaders() });
      setBetaRecruiting(next);
      toast.success(next ? 'Набор в бету включён' : 'Набор выключен');
    } catch { toast.error('Ошибка'); }
  };

  const createUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) { toast.error('Заполните все поля'); return; }
    try {
      const payload = {
        name: newUser.name, email: newUser.email, password: newUser.password,
        role: newUser.role, plan: newUser.role === 'owner' ? 'clinic' : newUser.plan,
      };
      if (newUser.role === 'owner') {
        if (newUser.custom_max_doctors) payload.custom_max_doctors = parseInt(newUser.custom_max_doctors);
        if (newUser.custom_max_admins) payload.custom_max_admins = parseInt(newUser.custom_max_admins);
      }
      await api.post('/admin/create-user', payload, { headers: adminHeaders() });
      toast.success(`${newUser.role === 'owner' ? 'Организация' : 'Пользователь'} ${newUser.email} создан(а)`);
      setCreateUserOpen(false);
      setNewUser({ name: '', email: '', password: '', role: 'doctor', plan: 'start', custom_max_doctors: '', custom_max_admins: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
  };

  const generateDemo = async () => {
    setDemoGenerating(true);
    setDemoProgress('Запуск...');
    setDemoLog([]);
    setDemoPercent(0);
    try {
      await api.post('/admin/generate-demo', {}, { headers: adminHeaders() });
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get('/admin/demo-status', { headers: adminHeaders() });
          if (data.progress && data.total) {
            setDemoPercent(Math.round((data.progress / data.total) * 100));
            setDemoProgress(`${data.progress}/${data.total}: ${data.current || ''}`);
          }
          if (data.log) setDemoLog(data.log);
          if (data.status === 'done' || data.status?.startsWith('error')) {
            clearInterval(poll);
            setDemoGenerating(false);
            setDemoPercent(100);
            if (data.status === 'done') { toast.success('Демо создано!'); fetchData(); }
            else toast.error(data.status);
          }
        } catch { clearInterval(poll); setDemoGenerating(false); }
      }, 3000);
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); setDemoGenerating(false); }
  };

  const clearDemo = async () => {
    if (!confirm('Удалить ВСЕ демо-визитки?')) return;
    try {
      const { data } = await api.delete('/admin/clear-demo', { headers: adminHeaders() });
      toast.success(`Удалено ${data.deleted} демо-аккаунтов`);
      fetchData();
    } catch { toast.error('Ошибка удаления'); }
  };

  const filtered = psychologists.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.email?.toLowerCase().includes(search.toLowerCase()) ||
    (p.psychologist_name || '').toLowerCase().includes(search.toLowerCase())
  );

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--app-sidebar)' }}>
        <Card className="w-full max-w-sm bg-stone-900 border-stone-800">
          <CardContent className="p-8">
            <div className="flex items-center gap-2 mb-6">
              <img src="/logo.png" alt="" className="w-12 h-12 rounded-lg object-cover" />
              <h1 className="font-heading text-xl text-stone-200">Админ-панель</h1>
            </div>
            <div className="space-y-4">
              <Input data-testid="admin-key" type="password" value={key} onChange={e => setKey(e.target.value)}
                placeholder="Секретный ключ" className="bg-stone-950/50 border-stone-800 text-stone-200"
                onKeyDown={e => e.key === 'Enter' && login()} />
              <Button data-testid="admin-login-btn" onClick={login} className="w-full text-white" style={{ background: 'var(--app-accent)' }}>Войти</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 lg:p-8" style={{ background: 'var(--app-bg)' }}>
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="" className="w-12 h-12 rounded-lg object-cover" />
            <h1 className="font-heading text-2xl text-stone-200">Админ-панель</h1>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { localStorage.removeItem('admin_token'); setAuthed(false); setToken(''); }}
            className="text-stone-500 hover:text-red-400">Выход</Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard icon={Users} label="Психологов" value={stats.total_psychologists} />
            <StatCard icon={Users} label="Клиентов" value={stats.total_patients} />
            <StatCard icon={CalendarDays} label="Записей" value={stats.total_appointments} />
            <StatCard icon={Crown} label="Платных" value={(stats.plan_distribution?.practik || 0) + (stats.plan_distribution?.profi || 0)}
              sub={`Старт: ${stats.plan_distribution?.start || 0}`} />
          </div>
        )}

        {/* Plan availability toggles */}
        <Card className="bg-stone-900 border-stone-800 mb-6">
          <CardContent className="p-4">
            <h3 className="text-stone-300 text-sm font-semibold mb-3">Доступность тарифов для покупки</h3>
            <div className="flex flex-wrap gap-4">
              {['practic', 'profi', 'clinic'].map(pid => (
                <label key={pid} className="flex items-center gap-2 cursor-pointer">
                  <Switch checked={!disabledPlans.includes(pid)} onCheckedChange={() => togglePlanDisabled(pid)} />
                  <span className="text-stone-400 text-sm">{PLAN_STYLES[pid].label}</span>
                  {disabledPlans.includes(pid) && <Badge className="bg-red-900/30 text-red-400 border border-red-900/50 text-[10px]">Скрыт</Badge>}
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Beta + Create User */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <Card className="bg-stone-900 border-stone-800">
            <CardContent className="p-4">
              <h3 className="text-stone-300 text-sm font-semibold mb-3">Закрытая бета</h3>
              <div className="space-y-3">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="text-stone-400 text-sm">Закрыть регистрацию</p>
                    <p className="text-stone-600 text-xs">Только через админку</p>
                  </div>
                  <Switch checked={closedBeta} onCheckedChange={toggleBeta} />
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="text-stone-400 text-sm">Набор в бета-тест</p>
                    <p className="text-stone-600 text-xs">Форма записи + 3 мес. Практика</p>
                  </div>
                  <Switch checked={betaRecruiting} onCheckedChange={toggleRecruiting} />
                </label>
              </div>
              {betaSignups.length > 0 && (
                <div className="mt-3 pt-3 border-t border-stone-800">
                  <p className="text-stone-500 text-xs mb-2">Заявки ({betaSignups.length}):</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {betaSignups.map(s => (
                      <div key={s.id} className="flex items-center justify-between text-xs">
                        <span className="text-stone-300">{s.email}</span>
                        <span className="text-stone-600">{s.created_at?.split('T')[0]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          <Card className="bg-stone-900 border-stone-800">
            <CardContent className="p-4">
              <h3 className="text-stone-300 text-sm font-semibold mb-3">Создать пользователя</h3>
              <p className="text-stone-600 text-xs mb-3">Ручная регистрация (работает всегда)</p>
              <Button size="sm" onClick={() => setCreateUserOpen(true)} style={{ background: 'var(--app-accent)' }} className="text-white w-full">
                <Plus size={14} className="mr-1" /> Создать аккаунт
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Demo Data */}
        <Card className="bg-stone-900 border-stone-800 mb-6">
          <CardContent className="p-4">
            <h3 className="text-stone-300 text-sm font-semibold mb-2">Демо-визитки</h3>
            <p className="text-stone-600 text-xs mb-3">6 специалистов + 3 клиники с AI-аватарами</p>
            <div className="flex gap-2 mb-3">
              <Button data-testid="generate-demo-btn" size="sm" disabled={demoGenerating}
                onClick={generateDemo}
                className="text-white flex-1" style={{ background: 'var(--app-accent)' }}>
                {demoGenerating ? 'Генерация...' : 'Сгенерировать демо'}
              </Button>
              <Button data-testid="clear-demo-btn" size="sm" variant="outline" disabled={demoGenerating}
                onClick={clearDemo}
                className="border-red-900/50 text-red-400 hover:bg-red-900/20">
                <Trash2 size={14} className="mr-1" /> Удалить
              </Button>
            </div>
            {demoGenerating && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-amber-400">{demoProgress}</span>
                  <span className="text-stone-500">{demoPercent}%</span>
                </div>
                <div className="h-1.5 bg-stone-800 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${demoPercent}%`, background: 'var(--app-accent)' }} />
                </div>
                {demoLog.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto bg-stone-950 rounded p-2 text-[10px] font-mono text-stone-500 space-y-0.5">
                    {demoLog.map((line, i) => (
                      <div key={i} className={line.includes('восстановлен') || line.includes('создан') ? 'text-green-500' : line.includes('ОК') ? 'text-stone-600' : 'text-stone-400'}>
                        {line}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notification Contacts */}
        <Card className="bg-stone-900 border-stone-800 mb-6">
          <CardContent className="p-4">
            <h3 className="text-stone-300 text-sm font-semibold mb-3">Уведомления о заявках на услуги</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-stone-500 text-xs mb-1 block">Telegram Chat ID</label>
                <Input data-testid="notify-tg" value={notifyTg} onChange={e => setNotifyTg(e.target.value)}
                  placeholder="715022267" className="bg-stone-950/50 border-stone-800 text-stone-200 text-sm" />
              </div>
              <div>
                <label className="text-stone-500 text-xs mb-1 block">Email</label>
                <Input data-testid="notify-email" value={notifyEmail} onChange={e => setNotifyEmail(e.target.value)}
                  placeholder="email@example.com" className="bg-stone-950/50 border-stone-800 text-stone-200 text-sm" />
              </div>
            </div>
            <Button size="sm" onClick={async () => {
              try {
                await api.put(`/admin/config?disabled_plans=${disabledPlans.join(',')}&closed_beta=${closedBeta}&beta_recruiting=${betaRecruiting}&service_notify_telegram=${notifyTg}&service_notify_email=${notifyEmail}`, {}, { headers: adminHeaders() });
                toast.success('Контакты сохранены');
              } catch { toast.error('Ошибка'); }
            }} className="text-white" style={{ background: 'var(--app-accent)' }}>
              Сохранить контакты
            </Button>
          </CardContent>
        </Card>

        {/* Search + View toggle */}
        <div className="flex gap-3 mb-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-600" />
            <Input data-testid="admin-search" placeholder="Поиск по имени или email..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-stone-900 border-stone-800 text-stone-200 placeholder:text-stone-600" />
          </div>
        </div>

        {/* Grouped view: Organizations first, then Solo practitioners */}
        {(() => {
          // Build org groups: owner → {owner, doctors, admins}
          const owners = filtered.filter(p => p.role === 'owner');
          const staffMap = {}; // owner_id → [staff]
          filtered.forEach(p => {
            if (p.owner_id) {
              if (!staffMap[p.owner_id]) staffMap[p.owner_id] = [];
              staffMap[p.owner_id].push(p);
            }
          });
          const soloUsers = filtered.filter(p => !p.owner_id && p.role !== 'owner');
          // Owners without staff (solo owners)
          const orgOwners = owners.filter(o => (staffMap[o.id] || []).length > 0 || o.plan === 'clinic');
          const soloOwners = owners.filter(o => (staffMap[o.id] || []).length === 0 && o.plan !== 'clinic');

          return (
            <div className="space-y-4">
              {/* Organizations */}
              {orgOwners.length > 0 && (
                <div>
                  <p className="text-stone-500 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Crown size={12} /> Организации ({orgOwners.length})
                  </p>
                  <div className="space-y-3">
                    {orgOwners.map(owner => {
                      const staff = staffMap[owner.id] || [];
                      const doctors = staff.filter(s => s.role === 'doctor');
                      const admins = staff.filter(s => s.role === 'admin');
                      const ps = PLAN_STYLES[owner.plan] || PLAN_STYLES.start;
                      return (
                        <Card key={owner.id} className="bg-stone-900 border-stone-800">
                          <CardContent className="p-0">
                            {/* Owner header */}
                            <div className="flex items-center justify-between p-4 border-b border-stone-800/50">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-teal-900/20 flex items-center justify-center">
                                  <Crown size={18} className="text-teal-400" />
                                </div>
                                <div>
                                  <p className="text-stone-200 font-medium text-sm">{owner.psychologist_name || owner.name}</p>
                                  <p className="text-stone-500 text-xs">{owner.email}</p>
                                </div>
                                <Badge className={`${ps.cls} text-xs ml-2`}>{ps.label}</Badge>
                                {owner.plan_expires_at && <span className="text-stone-600 text-[10px]">до {owner.plan_expires_at?.split('T')[0]}</span>}
                                {owner.banned && <Badge className="bg-red-900/30 text-red-400 border border-red-900/50 text-[10px]">Бан</Badge>}
                              </div>
                              <div className="flex items-center gap-3 text-xs text-stone-500">
                                <span>{doctors.length} врач.</span>
                                <span>{admins.length} адм.</span>
                                <span>{owner.patient_count || 0} пац.</span>
                                <span>{owner.appointment_count || 0} зап.</span>
                                <UserActions p={owner} {...{giftOpen, setGiftOpen, setGiftPlan, resetPlan, toggleBan, deletePsych, verifyEmail}} />
                              </div>
                            </div>
                            {/* Staff list */}
                            {staff.length > 0 && (
                              <div className="divide-y divide-stone-800/30">
                                {staff.map(s => {
                                  const sps = PLAN_STYLES[s.plan] || PLAN_STYLES.start;
                                  return (
                                    <div key={s.id} className="flex items-center justify-between px-4 py-2.5 pl-14 hover:bg-stone-800/20">
                                      <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${s.role === 'admin' ? 'bg-amber-900/20 text-amber-500' : 'bg-stone-800 text-stone-500'}`}>
                                          {s.role === 'admin' ? 'Админ' : 'Врач'}
                                        </span>
                                        <span className="text-stone-300 text-sm truncate">{s.psychologist_name || s.name}</span>
                                        <span className="text-stone-600 text-xs truncate">{s.email}</span>
                                        {s.banned && <Badge className="bg-red-900/30 text-red-400 border border-red-900/50 text-[10px]">Бан</Badge>}
                                      </div>
                                      <div className="flex items-center gap-3 text-xs text-stone-600 shrink-0">
                                        <span>{s.patient_count || 0} пац.</span>
                                        <span>{s.appointment_count || 0} зап.</span>
                                        <UserActions p={s} {...{giftOpen, setGiftOpen, setGiftPlan, resetPlan, toggleBan, deletePsych, verifyEmail}} />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Solo practitioners (owners without staff + doctors without owner) */}
              {(soloOwners.length + soloUsers.length) > 0 && (
                <div>
                  <p className="text-stone-500 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Users size={12} /> Соло-специалисты ({soloOwners.length + soloUsers.length})
                  </p>
                  <Card className="bg-stone-900 border-stone-800">
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-stone-800">
                            <TableHead className="text-stone-500">Имя</TableHead>
                            <TableHead className="text-stone-500">Email</TableHead>
                            <TableHead className="text-stone-500">Тариф</TableHead>
                            <TableHead className="text-stone-500">Роль</TableHead>
                            <TableHead className="text-stone-500">Пац.</TableHead>
                            <TableHead className="text-stone-500">Зап.</TableHead>
                            <TableHead className="text-stone-500">Рег.</TableHead>
                            <TableHead className="text-stone-500 text-right">Действия</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {[...soloOwners, ...soloUsers].map(p => {
                            const plan = p.plan || 'start';
                            const ps = PLAN_STYLES[plan] || PLAN_STYLES.start;
                            const isBanned = p.banned;
                            return (
                              <TableRow key={p.id} className={`border-stone-800/50 hover:bg-stone-800/30 ${isBanned ? 'opacity-50' : ''}`}>
                                <TableCell className="text-stone-200 font-medium text-sm">
                                  {p.psychologist_name || p.name}
                                  {isBanned && <Badge className="ml-2 bg-red-900/30 text-red-400 border border-red-900/50 text-[10px]">Бан</Badge>}
                                </TableCell>
                                <TableCell className="text-stone-400 text-sm">{p.email}</TableCell>
                                <TableCell><Badge className={`${ps.cls} text-xs`}>{ps.label}</Badge></TableCell>
                                <TableCell><span className="text-stone-600 text-xs">{p.role === 'owner' ? 'Owner' : 'Врач'}</span></TableCell>
                                <TableCell className="text-stone-400 text-sm">{p.patient_count}</TableCell>
                                <TableCell className="text-stone-400 text-sm">{p.appointment_count}</TableCell>
                                <TableCell className="text-stone-600 text-xs">{p.created_at?.split('T')[0]}</TableCell>
                                <TableCell className="text-right">
                                  <UserActions p={p} {...{giftOpen, setGiftOpen, setGiftPlan, resetPlan, toggleBan, deletePsych, verifyEmail}} />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          );
        })()}

        {/* Gift dialog */}
        <Dialog open={!!giftOpen} onOpenChange={() => setGiftOpen(null)}>
          <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-sm">
            <DialogHeader><DialogTitle className="font-heading text-stone-200">Подарить подписку</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-stone-400 text-sm">{psychologists.find(p => p.id === giftOpen)?.psychologist_name || psychologists.find(p => p.id === giftOpen)?.name}</p>
              <div>
                <label className="text-stone-400 text-sm font-medium mb-2 block">Тариф</label>
                <Select value={giftPlan} onValueChange={setGiftPlan}>
                  <SelectTrigger className="bg-stone-950/50 border-stone-800 text-stone-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-800">
                    <SelectItem value="practic">Практик (690 руб/мес)</SelectItem>
                    <SelectItem value="profi">Профи (1 690 руб/мес)</SelectItem>
                    <SelectItem value="clinic">Организация (индивидуально)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-stone-400 text-sm font-medium mb-2 block">На сколько дней? (0 = бессрочно)</label>
                <Input data-testid="gift-days" type="number" min={0} value={giftDays} onChange={e => setGiftDays(parseInt(e.target.value) || 0)}
                  className="bg-stone-950/50 border-stone-800 text-stone-200" />
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setGiftOpen(null)} className="flex-1 bg-stone-800 text-stone-300">Отмена</Button>
                <Button onClick={giftPlanTo} className="flex-1 text-white" style={{ background: 'var(--app-accent)' }}>
                  <Gift size={14} className="mr-1" /> Подарить
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create user dialog */}
        <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
          <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-sm">
            <DialogHeader><DialogTitle className="font-heading text-stone-200">
              {newUser.role === 'owner' ? 'Создать клинику' : 'Создать пользователя'}
            </DialogTitle></DialogHeader>
            <div className="space-y-3">
              {/* Role selector */}
              <div>
                <label className="text-stone-500 text-xs mb-1.5 block">Тип аккаунта</label>
                <div className="flex gap-2">
                  <button onClick={() => setNewUser(u => ({ ...u, role: 'doctor', plan: 'start' }))}
                    className={`flex-1 py-2 rounded-lg text-sm border transition-all ${
                      newUser.role === 'doctor' ? 'bg-amber-700/15 border-amber-700/50 text-amber-400' : 'bg-stone-950/50 border-stone-800 text-stone-500 hover:border-stone-700'
                    }`}>Специалист</button>
                  <button onClick={() => setNewUser(u => ({ ...u, role: 'owner', plan: 'clinic' }))}
                    className={`flex-1 py-2 rounded-lg text-sm border transition-all ${
                      newUser.role === 'owner' ? 'bg-teal-700/15 border-teal-700/50 text-teal-400' : 'bg-stone-950/50 border-stone-800 text-stone-500 hover:border-stone-700'
                    }`}>Организация (Owner)</button>
                </div>
              </div>
              <Input data-testid="create-user-name" value={newUser.name} onChange={e => setNewUser(u => ({ ...u, name: e.target.value }))}
                placeholder={newUser.role === 'owner' ? 'Название клиники' : 'Имя'} className="bg-stone-950/50 border-stone-800 text-stone-200" />
              <Input data-testid="create-user-email" value={newUser.email} onChange={e => setNewUser(u => ({ ...u, email: e.target.value }))} placeholder="Email" type="email"
                className="bg-stone-950/50 border-stone-800 text-stone-200" />
              <Input data-testid="create-user-password" value={newUser.password} onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))} placeholder="Пароль"
                className="bg-stone-950/50 border-stone-800 text-stone-200" />
              {newUser.role === 'doctor' && (
                <div>
                  <label className="text-stone-500 text-xs mb-1.5 block">Тариф</label>
                  <Select value={newUser.plan} onValueChange={v => setNewUser(u => ({ ...u, plan: v }))}>
                    <SelectTrigger className="bg-stone-950/50 border-stone-800 text-stone-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-stone-900 border-stone-800">
                      <SelectItem value="start">Старт (бесплатно)</SelectItem>
                      <SelectItem value="practic">Практик</SelectItem>
                      <SelectItem value="profi">Профи</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {newUser.role === 'owner' && (
                <div className="space-y-3 p-3 rounded-lg border border-teal-900/40 bg-teal-950/10">
                  <p className="text-teal-400 text-xs font-medium">Настройки клиники</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-stone-500 text-xs mb-1 block">Макс. врачей</label>
                      <Input data-testid="create-clinic-max-doctors" type="number" min="1" value={newUser.custom_max_doctors}
                        onChange={e => setNewUser(u => ({ ...u, custom_max_doctors: e.target.value }))} placeholder="Без лимита"
                        className="bg-stone-950/50 border-stone-800 text-stone-200 text-sm" />
                    </div>
                    <div>
                      <label className="text-stone-500 text-xs mb-1 block">Макс. админов</label>
                      <Input data-testid="create-clinic-max-admins" type="number" min="1" value={newUser.custom_max_admins}
                        onChange={e => setNewUser(u => ({ ...u, custom_max_admins: e.target.value }))} placeholder="Без лимита"
                        className="bg-stone-950/50 border-stone-800 text-stone-200 text-sm" />
                    </div>
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setCreateUserOpen(false)} className="flex-1 bg-stone-800 text-stone-300">Отмена</Button>
                <Button data-testid="create-user-submit" onClick={createUser}
                  className={`flex-1 text-white ${newUser.role === 'owner' ? 'bg-teal-700 hover:bg-teal-600' : ''}`}
                  style={newUser.role !== 'owner' ? { background: 'var(--app-accent)' } : {}}>
                  {newUser.role === 'owner' ? 'Создать клинику' : 'Создать'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <Card className="bg-stone-900 border-stone-800">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-stone-500 text-xs uppercase tracking-wider">{label}</span>
          <Icon size={16} className="text-stone-600" />
        </div>
        <p className="text-2xl font-bold text-stone-200">{value}</p>
        {sub && <p className="text-stone-600 text-xs mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}


function UserActions({ p, giftOpen, setGiftOpen, setGiftPlan, resetPlan, toggleBan, deletePsych, verifyEmail }) {
  const plan = p.plan || 'start';
  const isBanned = p.banned;
  return (
    <div className="flex items-center gap-0.5">
      {p.email_verified === false && (
        <Button variant="ghost" size="sm" onClick={() => verifyEmail(p.id)}
          className="text-green-600 hover:text-green-400 h-7 w-7 p-0" title="Подтвердить почту">
          <MailCheck size={13} />
        </Button>
      )}
      {plan === 'start' ? (
        <Button variant="ghost" size="sm" onClick={() => { setGiftOpen(p.id); setGiftPlan('practic'); }}
          className="text-amber-600 hover:text-amber-400 h-7 w-7 p-0" title="Подарить подписку">
          <Gift size={13} />
        </Button>
      ) : (
        <Button variant="ghost" size="sm" onClick={() => resetPlan(p.id)}
          className="text-stone-600 hover:text-stone-400 h-7 w-7 p-0" title="Сбросить на Старт">
          <RotateCcw size={13} />
        </Button>
      )}
      <Button variant="ghost" size="sm" onClick={() => toggleBan(p.id, !isBanned)}
        className={`h-7 w-7 p-0 ${isBanned ? 'text-green-600 hover:text-green-400' : 'text-orange-600 hover:text-orange-400'}`}
        title={isBanned ? 'Разбанить' : 'Забанить'}>
        {isBanned ? <CheckCircle size={13} /> : <Ban size={13} />}
      </Button>
      <Button variant="ghost" size="sm" onClick={() => deletePsych(p.id, p.name)}
        className="text-stone-600 hover:text-red-400 h-7 w-7 p-0" title="Удалить">
        <Trash2 size={13} />
      </Button>
    </div>
  );
}
